import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useBeforeUnload, useParams } from "react-router-dom";

import { Questionnaire, QuestionnaireItem, QuestionnaireResponse, Task } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Drawer, Flex, Group, rem } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import * as Sentry from "@sentry/react";
import isEqual from "lodash/isEqual";

import EddForm from "@/clients/components/ObstetricalProfile/EDDForm";
import {
  createEDDInitialValues,
  createEDDQuestionnaireResponse,
  fetchEDDQuestionnaire,
  fetchEDDQuestionnaireResponse,
  INITIAL_VALUES,
} from "@/clients/services/edd";
import { eddDisplayDateRefinement } from "@/clients/zod-schemas/edd-form";
import { DisabledReasonButton, NO_CHANGES_REASON } from "@/components/DisabledReason";
import { fetchAndDispatchCurrentPregnancyRecord } from "@/pregnancy/services/record";
import { useAppDispatch } from "@/store";
import { selectCurrentPregnancyRecord } from "@/store/slices/pregnancy";
import { showSystemErrorNotification } from "@/utils/notifications";
import { getQuestionnaireSchema, QuestionnaireFormValues } from "@/utils/questionnaires";

interface EDDDrawerProps {
  opened: boolean;
  onClose: () => void;
  isCurrentlyPregnant: boolean;
  refreshTrigger?: number;
  postpartumTrackerTask?: Task;
  onPostpartumTrackerComplete?: () => void;
}

export function EDDDrawer({
  opened,
  onClose,
  isCurrentlyPregnant,
  refreshTrigger,
  postpartumTrackerTask,
  onPostpartumTrackerComplete,
}: EDDDrawerProps) {
  const medplum = useMedplum();
  const { clientId } = useParams() as { clientId: string };
  const activePregnancyRecord = useSelector(selectCurrentPregnancyRecord(clientId));
  const pregnancyRecordId = activePregnancyRecord?.list?.id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialValues, setInitialValues] = useState<QuestionnaireFormValues>();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const [_questionnaireResponse, setQuestionnaireResponse] = useState<
    QuestionnaireResponse | undefined
  >();
  const dispatch = useAppDispatch();

  useBeforeUnload((event: BeforeUnloadEvent) => {
    if (isSubmitting) event.preventDefault();
  });

  const [questionnaireGroup, schema] = useMemo(() => {
    const questionnaireGroup = questionnaire?.item?.[0];
    const baseSchema = questionnaireGroup?.item
      ? getQuestionnaireSchema(questionnaireGroup.item)
      : null;

    const schema = baseSchema
      ? baseSchema.superRefine((data, ctx) =>
          eddDisplayDateRefinement(data as Record<string, unknown>, ctx),
        )
      : null;

    return [questionnaireGroup, schema];
  }, [questionnaire]);

  const form = useForm({
    initialValues: INITIAL_VALUES as QuestionnaireFormValues,
    validate: schema ? zodResolver(schema) : undefined,
  });

  const formSetValues = form.setValues;
  const formValues = form.values;

  useEffect(() => {
    if (!opened) return;
    form.reset();
    (async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      const questionnaire = await fetchEDDQuestionnaire(medplum);
      setQuestionnaire(questionnaire);

      const questionnaireResponse = await fetchEDDQuestionnaireResponse(
        medplum,
        questionnaire,
        clientId,
      );
      setQuestionnaireResponse(questionnaireResponse);

      try {
        const initialValues = await createEDDInitialValues(
          medplum,
          questionnaireResponse,
          pregnancyRecordId,
        );
        setInitialValues(initialValues);
        // Coerce undefined date fields to null after merging so DateInput stays
        // in controlled mode. In Mantine v8, value={undefined} transitions to
        // uncontrolled, which can retain stale internal state or display a
        // fallback date. value={null} keeps the field controlled and empty.
        formSetValues({
          ...INITIAL_VALUES,
          ...initialValues,
          lmpDate: initialValues.lmpDate ?? null,
          lmpEddDate: initialValues.lmpEddDate ?? null,
          ultrasoundDate: initialValues.ultrasoundDate ?? null,
          ultrasoundEddDate: initialValues.ultrasoundEddDate ?? null,
          customEddDate: initialValues.customEddDate ?? null,
        } as QuestionnaireFormValues);
      } catch (error) {
        showSystemErrorNotification();
        Sentry.captureException(error);
      }
    })();
    // form intentionally omitted: including it causes infinite loop (form.reset() triggers re-render)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSetValues, medplum, clientId, pregnancyRecordId, refreshTrigger, opened]);

  const hasChanges = useMemo(
    () => (initialValues ? !isEqual(initialValues, formValues) : false),
    [initialValues, formValues],
  );
  const saveDisabledReason = getSaveDisabledReason(
    hasChanges,
    isCurrentlyPregnant || Boolean(form.values.isPregnant),
  );

  const handleSubmit = async (values: QuestionnaireFormValues) => {
    if (!schema || !questionnaire) return;
    setIsSubmitting(true);
    try {
      const parsedValues = schema.parse(values);

      const updatedQuestionnaireResponse = await createEDDQuestionnaireResponse(
        medplum,
        questionnaire,
        clientId,
        parsedValues,
      );
      setQuestionnaireResponse(updatedQuestionnaireResponse);

      // If opened from postpartum tracker, mark the task as completed
      if (postpartumTrackerTask?.id) {
        try {
          await medplum.updateResource({
            ...postpartumTrackerTask,
            status: "completed",
          });
          onPostpartumTrackerComplete?.();
        } catch (error) {
          showSystemErrorNotification();
          Sentry.captureException(error);
        }
      }

      setInitialValues(values);

      const refreshedPregnancyRecord = await fetchAndDispatchCurrentPregnancyRecord(
        medplum,
        dispatch,
        clientId,
        {
          previousVersionId: activePregnancyRecord?.list.meta?.versionId,
          maxRetries: 40,
          retryWhenMissing: true,
        },
      );
      if (!refreshedPregnancyRecord) {
        throw new Error("Pregnancy record was not available after saving EDD");
      }
    } finally {
      setIsSubmitting(false);
    }
    onClose();
  };

  const buttonText = postpartumTrackerTask ? "Start new Pregnancy" : "Save";

  return (
    <Drawer.Root
      trapFocus
      size={512}
      opened={opened}
      position="right"
      onClose={onClose}
      closeOnClickOutside={false}
    >
      <Drawer.Overlay />
      <Drawer.Content data-testid="edd-drawer">
        <form onSubmit={form.onSubmit((values, _) => handleSubmit(values))}>
          <Drawer.Header px="md" h={rem(64)} bg="gray.0">
            <Group w="100%" justify="space-between">
              <Group gap="xs">
                <Drawer.CloseButton data-testid="edd-drawer-close-button" />
                <Drawer.Title>Update EDD</Drawer.Title>
              </Group>
              <DisabledReasonButton
                type="submit"
                miw="fit-content"
                loading={isSubmitting}
                disabledReason={saveDisabledReason}
                data-testid="edd-drawer-save-button"
              >
                {buttonText}
              </DisabledReasonButton>
            </Group>
          </Drawer.Header>
          <Drawer.Body p="lg">
            <Flex gap="lg" direction="column">
              <EddForm
                form={form}
                groupTitleOrder={3}
                isCurrentlyPregnant={isCurrentlyPregnant}
                eddSelectGroup={
                  questionnaireGroup?.item?.find(
                    (item) => item.linkId === "eddDisplay",
                  ) as QuestionnaireItem
                }
              />
            </Flex>
          </Drawer.Body>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
}

/**
 * Why Save can't run yet, if it can't.
 *
 * @param hasChanges - Whether the form differs from what was loaded.
 * @param isPregnant - Whether the client is pregnant already or is being marked pregnant here.
 * @returns The reason to show on the disabled Save, or undefined when Save can run.
 */
function getSaveDisabledReason(hasChanges: boolean, isPregnant: boolean): string | undefined {
  if (!hasChanges) return NO_CHANGES_REASON;
  if (!isPregnant) return NOT_PREGNANT_REASON;
  return undefined;
}

const NOT_PREGNANT_REASON = 'Turn on "Is the client pregnant?" first';
